import { DocumentData, DocumentSnapshot } from "firebase/firestore";
import { LessonExtensionSchema } from "../../model/LessonExtension";
import {
  ASFCourseSession,
  ASFCourseSessionAttendance,
  AutovioCalendarEvent,
  BaseAutovioCalendarEvent,
  CourseLesson,
  DrivingLesson,
  OtherEvent,
  PaidLessonStudentAttendance,
  RemoteLesson,
  TheoryExam,
  TheoryLesson,
  TheoryLessonStudentAttendance,
  VehicleEvent,
} from "../../model/autovioCalendarEvents";
import { fromFirestore, normalizeStreet } from "../../backoffice.utils";
import { drivingSchoolsProvider } from "../drivingSchoolsProvider";
import type { PostalAddress } from "../../model/PostalAddress";
import type { OnlineMeeting } from "../../model/OnlineMeeting";
import { DateTime } from "luxon";
import { theoryLectureAbbreviation, theoryUnitToTheoryLecture } from "../../model/TheoryLecture";
import { z } from "zod";

const BillingWhenSchema = z.literal("asap").nullish();
const PaymentStatusSchema = z.enum(["pending", "success", "failed", "disputeLost", "refunded"]).nullish();

export const autovioCalendarEventConverter = {
  toFirestore: (_: TheoryLesson): DocumentData => {
    throw new Error("Not implemented");
  },

  fromFirestore: (doc: DocumentSnapshot): AutovioCalendarEvent => {
    const data = doc.data();
    if (!data) {
      throw Error(`Document ${doc.ref.path} does not exist`);
    }
    const calendarEvent = autovioCalendarEventConverter.fromFirestoreData(data);
    if (calendarEvent.id !== doc.id) {
      throw Error(`Unexpected uid in Firestore document ${doc.ref.path}: ${calendarEvent.id}`);
    }
    return calendarEvent;
  },

  fromFirestoreData: (data: DocumentData): AutovioCalendarEvent => {
    const createdAt = fromFirestore(data.createdAt) as DateTime | undefined;
    const updatedAt = fromFirestore(data.updatedAt) as DateTime | undefined;
    const base: BaseAutovioCalendarEvent = {
      id: data.uid,
      start: fromFirestore(data.start),
      end: fromFirestore(data.end),
      drivingSchoolId: data.drivingSchoolUid,
      branchId: data.branchId,
      bufferTimes: data.bufferTimes ?? {
        wrapUpTime: 0,
        preparationTime: 0,
      },
      resources: data.resources,
      deletionReason: data.deletionReason,
      editCount: data.editCount ?? (createdAt ? (updatedAt && createdAt < updatedAt ? 1 : 0) : updatedAt ? 1 : 0),
      editReason: data.editReason,
      editWithReasonCount: data.editWithReasonCount ?? 0,
      createdAt: fromFirestore(data.createdAt),
      createdBy: data.createdBy,
      updatedAt: fromFirestore(data.updatedAt),
      updatedBy: data.updatedBy,
      replacedBy: data.replacedBy,
      deleted: data.deleted,
      deletedAt: fromFirestore(data.deletedAt),
      deletedBy: data.deletedBy,
      vogelId: data.vogelId,
    };
    if (data.type === "TheoryLesson") {
      const students = fromFirestore(data.students) as any as Record<string, TheoryLessonStudentAttendance>;
      const numBookedStudents = Object.values(students).filter((it) => it.rsvp === "accepted").length;
      const location = data.location as PostalAddress | OnlineMeeting;
      let { theoryLecture, theoryUnit } = data;
      if (theoryLecture) {
        if (theoryLecture.category === "Grundstoff") {
          theoryUnit = theoryLecture.lectureNumber;
        } else if (theoryLecture.category === "Zusatzstoff_A") {
          if (theoryLecture.lectureNumber > 4) {
            // Map 15-18 to 1-4 ...
            theoryLecture.lectureNumber -= 14;
          }
          theoryUnit = 14 + theoryLecture.lectureNumber;
        } else if (theoryLecture.category === "Zusatzstoff_B") {
          if (theoryLecture.lectureNumber > 2) {
            // Map 13-14 to 1-2 ...
            theoryLecture.lectureNumber -= 12;
          }
          theoryUnit = 12 + theoryLecture.lectureNumber;
        }
      } else {
        if (!theoryUnit) {
          throw new Error(`TheoryLesson ${data.uid} has neither theoryUnit nor theoryLecture`);
        } else if (typeof theoryUnit === "string") {
          theoryUnit = parseInt(theoryUnit, 10);
        }
        // For backwards compatibility: derive theoryLecture from theoryUnit ...
        theoryLecture = theoryUnitToTheoryLecture(theoryUnit as number);
      }
      let { branchId } = base;
      if (!branchId && location.type !== "OnlineMeeting") {
        // Try to determine the branchId from the location ...
        const drivingSchool = drivingSchoolsProvider.getOneFromCache(base.drivingSchoolId);
        if (drivingSchool) {
          let matchingBranches = drivingSchool.branches.filter(
            (branch) => branch.postalAddress.postalCode === location.postalCode,
          );
          if (matchingBranches.length === 1) {
            branchId = matchingBranches[0].id;
          } else if (matchingBranches.length > 1) {
            const expected = normalizeStreet(location.street);
            matchingBranches = matchingBranches.filter((branch) => branch.postalAddress.normalizedStreet === expected);
            if (matchingBranches.length === 1) {
              branchId = matchingBranches[0].id;
            }
          }
        }
      }
      return {
        ...base,
        courseUid: data.courseUid,
        theoryUnit,
        theoryLecture,
        theoryLectureAbbreviation: theoryLectureAbbreviation(theoryLecture),
        instructorId: data.instructor.uid,
        students,
        numBookedStudents,
        maxStudents: data.maxStudents,
        type: "TheoryLesson",
        location,
        branchId: branchId ?? "",
        noteForInstructor: data.notes ?? "",
        noteForStudents: data.remarks ?? "",
      } satisfies TheoryLesson;
    }
    if (data.type === "DrivingLesson") {
      const billingWhen = BillingWhenSchema.safeParse(data.billingWhen).data;
      const paymentStatus = PaymentStatusSchema.safeParse(data.paymentStatus).data;
      if (data.drivingLessonType === "theoretischePruefung") {
        return {
          ...base,
          type: "TheoryExam",
          student: fromFirestore(data.student) as any,
          drivingSchoolId: data.drivingSchoolUid,
          status: _theoryExamStatus(data),
          startLocation: data.startLocation,
          theoryExamResult: data.theoryExamResult,
          theoryExamResults: data.theoryExamResults,
          noteForInstructor: data.notes ?? "",
          noteForStudent: data.remarks ?? "",
          ...(billingWhen ? { billingWhen } : {}),
          ...(paymentStatus ? { paymentStatus } : {}),
        } satisfies TheoryExam;
      }
      let drivingLessonType: DrivingLesson["drivingLessonType"];
      const { additionalContent } = data;
      if (Array.isArray(additionalContent) && additionalContent.includes("instructionOnVehicle")) {
        drivingLessonType = "unterweisungAmFahrzeug";
      } else if (Array.isArray(additionalContent) && additionalContent.includes("practicalIntroduction")) {
        drivingLessonType = "praktischeUnterweisung";
      } else {
        drivingLessonType = data.drivingLessonType;
      }
      return {
        ...base,
        type: "DrivingLesson",
        drivingLessonType,
        drivingLicenseClass: data.drivingLicenseClass,
        extensions: data.extensions ? data.extensions.map((it: any) => LessonExtensionSchema.parse(it)) : [],
        instructorId: data.instructor?.uid,
        student: fromFirestore(data.student) as any,
        endedAt: fromFirestore(data.endedAt),
        isBooked: _isDrivingLessonBooked(data),
        status: _drivingLessonStatus(data),
        startedAt: fromFirestore(data.startedAt),
        startLocation: data.startLocation,
        price: { amount: data.price.studentPrice.gross / 100, currency: "EUR" },
        ...(data.isManualPrice === true ? { isManualPrice: true } : {}),
        practicalExamResult: data.practicalExamResult,
        vehicleId: data.vehicleId,
        vehicle2Id: data.vehicle2Id,
        studentUsesOwnMotorcycle: data.studentUsesOwnMotorcycle,
        noteForInstructor: data.notes ?? "",
        remarks: data.remarks ?? "",
        noteForStudent: data.remarks ?? "",
        ...(billingWhen ? { billingWhen } : {}),
        ...(paymentStatus ? { paymentStatus } : {}),
      } satisfies DrivingLesson;
    }
    if (data.type === "RemoteLesson") {
      return {
        ...base,
        type: "RemoteLesson",
        extensions: data.extensions ? data.extensions.map((it: any) => LessonExtensionSchema.parse(it)) : [],
        instructorId: data.instructor.uid,
        student: data.student,
        price: { amount: data.price.studentPrice.gross / 100, currency: "EUR" },
        noteForInstructor: data.notes ?? "",
        noteForStudent: data.remarks ?? "",
      } satisfies RemoteLesson;
    }
    if (data.type === "ASFCourseSession") {
      const participants =
        (fromFirestore(data.participants) as any as Record<string, ASFCourseSessionAttendance>) ?? {};
      return {
        ...base,
        type: "ASFCourseSession",
        sessionType: data.sessionType,
        courseUid: data.courseUid,
        instructorId: data.instructor.uid,
        numBookedParticipants: Object.keys(participants).length,
        participants,
        location: data.location,
        branchId: data.branchId ?? "",
        noteForInstructor: data.notes ?? "",
        noteForParticipants: data.remarks ?? "",
      } satisfies ASFCourseSession;
    }
    if (data.type === "CourseLesson") {
      return {
        ...base,
        type: "CourseLesson",
        courseId: data.course.id,
        courseModuleName: data.courseModule.name,
        courseModuleColor: data.courseModule.color,
        location: data.location,
        roomId: data.room ? `${base.drivingSchoolId}:${data.room.id}` : null,
        lecturerId: data.lecturer ? data.lecturer.id : null,
        instructorId: data.instructor?.uid,
        noteForInstructor: data.notes ?? "",
        noteForParticipants: data.noteForParticipants ?? data.remarks ?? "",
      } satisfies CourseLesson;
    }
    if (data.type === "Other") {
      return {
        ...base,
        type: "Other",
        instructorId: data.instructor.uid,
        categoryId: data.categoryId,
        notes: data.notes,
        otherEventDuration: data.otherEventDuration,
        vehicleId: data.vehicleId,
      } satisfies OtherEvent;
    }
    if (data.type === "VehicleEvent") {
      return {
        ...base,
        type: "VehicleEvent",
        vehicleEventType: data.vehicleEventType,
        noteForInstructor: data.noteForInstructor ?? "",
        vehicleId: data.vehicleId,
      } satisfies VehicleEvent;
    }
    // Fallback for unknown events ...
    return { ...base, type: "Unknown" };
  },
};

function _theoryExamStatus(data: DocumentData): TheoryExam["status"] {
  if (data.deleted && !data.replacedBy) {
    return "canceledByInstructor";
  }
  const { student } = data;
  const rsvp: PaidLessonStudentAttendance["rsvp"] = student.rsvp;
  if (rsvp === "pending") {
    return "invited";
  } else if (rsvp === "accepted") {
    // Derive the effective scalar result from the per-training `theoryExamResults` map (a failed result in any covered training takes precedence), falling back to the legacy scalar mirror.
    const perTrainingResults: Array<"passed" | "failed"> = Object.values(data.theoryExamResults ?? {});
    const effectiveResult =
      perTrainingResults.find((result) => result === "failed") ??
      perTrainingResults[0] ??
      (data.theoryExamResult as "passed" | "failed" | undefined);
    return effectiveResult ?? "booked";
  } else if (rsvp === "rejected") {
    // Heuristic to determine if an invitation was declined or a booked lesson was canceled ...
    return student.bookedAt || student.cancelReason || data.paymentIntentId ? "canceledByStudent" : "declinedByStudent";
  } else if (rsvp === "declined") {
    return "declinedByStudent";
  } else if (rsvp === "canceled") {
    return "canceledByStudent";
  } else if (rsvp === "no-show") {
    return "no-show";
  }
}

function _isDrivingLessonBooked(data: DocumentData): boolean {
  if (data.deleted) {
    return false;
  }
  const { student } = data;
  const rsvp: PaidLessonStudentAttendance["rsvp"] = student.rsvp;
  return rsvp === "accepted" || rsvp === "no-show";
}

function _drivingLessonStatus(data: DocumentData): DrivingLesson["status"] {
  if (data.deleted && !data.replacedBy) {
    return "canceledByInstructor";
  }
  const { student } = data;
  const rsvp: PaidLessonStudentAttendance["rsvp"] = student.rsvp;
  if (rsvp === "pending") {
    return "invited";
  } else if (rsvp === "accepted") {
    return (data.practicalExamResult as "passed" | "failed" | undefined) ?? (data.endedAt ? "finished" : "booked");
  } else if (rsvp === "rejected") {
    // Heuristic to determine if an invitation was declined or a booked lesson was canceled ...
    return student.bookedAt || student.cancelReason || data.paymentIntentId ? "canceledByStudent" : "declinedByStudent";
  } else if (rsvp === "declined") {
    return "declinedByStudent";
  } else if (rsvp === "canceled") {
    return "canceledByStudent";
  } else if (rsvp === "no-show") {
    return "no-show";
  }
}
